import { useEffect, useMemo, useState, useRef } from 'react';
import { View, TextInput, TouchableOpacity, ActivityIndicator, ScrollView, Image as RNImage, Alert, Share as NativeShare, StyleSheet } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import * as ImagePicker from 'expo-image-picker';
import { openAppSettings } from '@/utils/permissions';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import * as Haptics from 'expo-haptics';
import { Text } from '@/components';
import { useAuth } from '@/context/AuthContext';
import { useSaveFollowTrail } from '@/lib/queries/trails';
import { Trail } from '@/types/trail';
import { useTranslation } from 'react-i18next';
import { clearEndWalkDraft, loadEndWalkDraft } from '@/utils/endWalkDraft';
import { logEvent } from '@/lib/analytics';
import LottieLoader from '@/components/LottieLoader';
import styles from './follow-wrapup.styles';

export default function FollowWrapupPage() {
  const router = useRouter();
  const params = useLocalSearchParams();
  const insets = useSafeAreaInsets();
  const { session, user } = useAuth();
  const saveFollowTrailMutation = useSaveFollowTrail(session?.accessToken, user?.id);
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Trail | null>(null);
  const [photos, setPhotos] = useState<string[]>([]);
  const [reviewContent, setReviewContent] = useState('');
  const [rating, setRating] = useState(0);
  const [saving, setSaving] = useState(false);
  const draftId = typeof params?.draftId === 'string' ? params.draftId : undefined;

  useEffect(() => {
    if (!draftId) {
      const rawDraft = typeof params?.draft === 'string' ? decodeURIComponent(params.draft as string) : undefined;
      if (!rawDraft) return;
      try {
        const parsed = JSON.parse(rawDraft) as Trail;
        setDraft(parsed);
        setRating(typeof parsed?.rating === 'number' ? parsed.rating : 0);
        setReviewContent(parsed?.review || '');
      } catch {
        Alert.alert(t('common.error'), t('followWrapup.unableToLoad'));
      }
      return;
    }
    let mounted = true;
    loadEndWalkDraft(draftId).then((parsed) => {
      if (!mounted || !parsed) return;
      const parsedTrail = parsed as Trail;
      setDraft(parsedTrail);
      setRating(typeof parsedTrail.rating === 'number' ? parsedTrail.rating : 0);
      setReviewContent(parsedTrail.review || '');
    });
    return () => { mounted = false; };
  }, [draftId, params?.draft]);

  const sourceTrailId = useMemo(() => {
    if (typeof draft?.originalTrailId === 'string' && draft.originalTrailId.length > 0) {
      return draft.originalTrailId;
    }
    return undefined;
  }, [draft]);

  const pickImages = async () => {
    const { status: existingStatus } = await ImagePicker.getMediaLibraryPermissionsAsync();

    if (existingStatus === 'denied') {
      Alert.alert(
        t('endWalkInfo.photoLibraryAccessNeeded'),
        t('followWrapup.photoLibraryMessage'),
        [
          { text: t('common.cancel'), style: 'cancel' },
          { text: t('record.openSettings'), onPress: openAppSettings },
        ],
      );
      return;
    }

    if (existingStatus !== 'granted') {
      await new Promise<void>((resolve) => {
        Alert.alert(
          t('endWalkInfo.allowPhotoAccess'),
          t('followWrapup.allowPhotoAccessMessage'),
          [{ text: t('common.continue'), onPress: () => resolve() }],
          { cancelable: false },
        );
      });

      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== 'granted') {
        Alert.alert(
          t('endWalkInfo.photoLibraryAccessNeeded'),
          t('followWrapup.photoLibraryMessage'),
          [
            { text: t('common.cancel'), style: 'cancel' },
            { text: t('record.openSettings'), onPress: openAppSettings },
          ],
        );
        return;
      }
    }

    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        allowsMultipleSelection: true,
        quality: 0.6,
        mediaTypes: ['images'],
        // ponytail: iOS PHPicker fails to load HEIC originals (esp. iCloud) — force JPEG
        preferredAssetRepresentationMode: ImagePicker.UIImagePickerPreferredAssetRepresentationMode.Compatible,
      });
      if (!result.canceled) {
        const uris = (result.assets || []).map((asset) => asset.uri);
        setPhotos((prev) => [...prev, ...uris]);
      }
    } catch {
      Alert.alert(t('common.error'), t('trailEdit.failedToPickImages'));
    }
  };

  const clearRecordingState = async () => {
    try {
      await AsyncStorage.removeItem('recording_start_time');
      await AsyncStorage.removeItem('recording_coordinates');
      await AsyncStorage.removeItem('recording_max_elevation');
      await AsyncStorage.removeItem('recording_max_speed');
      await AsyncStorage.removeItem('recording_last_update');
    } catch {
      // ignore
    }
  };

  const handleOpenSharePopup = () => {
    if (true) Haptics.selectionAsync();
    router.push(`/end-walk/share?draftId=${draftId}` as any);
  };




  const handleDone = async () => {
    if (!draft || !sourceTrailId) {
      Alert.alert(t('common.error'), t('followWrapup.originalTrailNotFound'));
      return;
    }

    if (!rating) {
      Alert.alert(t('followWrapup.ratingRequired'), t('followWrapup.ratingRequiredMessage'));
      return;
    }

    setSaving(true);
    try {
      await saveFollowTrailMutation.mutateAsync({
        sourceTrailId,
        trail: {
          ...draft,
          rating,
          review: reviewContent.trim(),
        },
        review: {
          rating,
          ...(reviewContent.trim() ? { content: reviewContent.trim() } : {}),
        },
        photos: photos,
      });
      logEvent('walk_completed', { distance: draft?.distance, duration: draft?.duration, flow: 'follow' });

      if (true) {
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
      }

      await clearRecordingState();
      clearEndWalkDraft(draftId);
      router.replace('/');
    } catch (error: any) {
      console.error('Failed to save followed trail:', error);
      logEvent('walk_save_failed', { flow: 'follow' });
      if (true) {
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error);
      }
      Alert.alert(t('common.error'), error?.message || t('followWrapup.failedToSave'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />
      <View style={[styles.container, { paddingTop: insets.top + 20 || 20 }]}> 
        <ScrollView contentContainerStyle={styles.scroll}>
          <View style={styles.header}>
            <Text style={styles.title}>{t('followWrapup.title')}</Text>
            <Text style={styles.subtitle}>{t('followWrapup.addReviewSubtitle')}</Text>
          </View>

          <View style={styles.card}>
            <Text style={styles.sectionTitle}>{t('walkSummary.howWasTrail')}</Text>
            <Text style={styles.sectionSub}>{t('followWrapup.ratingReviewHelp')}</Text>
            <View style={styles.starsRow}>
              {[1, 2, 3, 4, 5].map((star) => {
                const active = star <= rating;
                return (
                  <TouchableOpacity
                    key={star}
                    style={[styles.starButton, active && styles.starButtonActive]}
                    onPress={() => setRating(star)}
                    disabled={saving}
                  >
                    <Text style={[styles.starText, active && styles.starTextActive]}>★</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
            <TextInput
              placeholder={t('followWrapup.whatDidYouThink')}
              placeholderTextColor="rgba(255,255,255,0.25)"
              value={reviewContent}
              onChangeText={setReviewContent}
              style={styles.input}
              editable={!saving}
              multiline
            />
          </View>

          <View style={styles.card}>
            <Text style={styles.sectionTitle}>{t('endWalkInfo.addPhotos')}</Text>
            <Text style={styles.sectionSub}>{t('followWrapup.shareHighlights')}</Text>

            <TouchableOpacity style={styles.addPhotoBox} onPress={pickImages} disabled={saving}>
              <Text style={styles.plusSign}>+</Text>
            </TouchableOpacity>

            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.photoRow}>
              {photos.map((photo, index) => {
                const extraStyle: any = { zIndex: index };
                if (index === 1) extraStyle.transform = [{ rotate: '-6deg' }, { translateY: 6 }];
                if (index === 2) extraStyle.transform = [{ rotate: '-3deg' }, { translateY: 3 }];
                return <RNImage key={`${photo}-${index}`} source={{ uri: photo }} style={[styles.thumb, extraStyle]} />;
              })}
            </ScrollView>
          </View>



          <View style={styles.bottomRow}>
            <TouchableOpacity style={styles.backButton} onPress={() => router.back()} disabled={saving}>
              <Text style={styles.backText}>{t('common.back')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.shareButton, saving && styles.shareButtonDisabled]}
              onPress={handleOpenSharePopup}
              disabled={saving}
            >
              <Text style={styles.shareButtonText}>{t('endWalkInfo.share')}</Text>
            </TouchableOpacity>
            
            
            <TouchableOpacity
              style={[styles.doneButton, saving && styles.doneButtonDisabled]}
              onPress={handleDone}
              disabled={saving}
            >
              {saving ? <ActivityIndicator color="#000" /> : <Text style={styles.doneText}>{t('followWrapup.finish')}</Text>}
            </TouchableOpacity>
          </View>
        </ScrollView>
      </View>

      {/* Full-screen blocking loader while uploading */}
      {saving && (
        <View style={StyleSheet.absoluteFill} pointerEvents="auto">
          <View style={styles.savingOverlay}>
            <LottieLoader size={120} />
            <Text style={styles.savingText}>{t('common.saving')}</Text>
          </View>
        </View>
      )}

    </>
  );
}
